/**
 * useVoiceDictation
 *
 * Mic → 16kHz PCM → backend WS (/api/transcribe/stream) → live transcript.
 * Exposes audio level for visual feedback and enforces guardrails to avoid
 * a forgotten mic burning Azure minutes:
 *   - Hard cap of MAX_SESSION_MS on a single session
 *   - Auto-stop after SILENCE_TIMEOUT_MS of no detected speech
 *   - Auto-stop when the tab becomes hidden
 *   - Stop + cleanup on unmount, page unload, or WS disconnect
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import {
  RealtimeTranscriptionService,
  type TranscriptionEvent,
} from '../services/realtime-transcription.service';

interface UseVoiceDictationResult {
  isRecording: boolean;
  isConnecting: boolean;
  transcript: string;
  interim: string;
  /** 0..1 normalized audio level for the input stream. */
  level: number;
  error: string | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  reset: () => void;
}

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL as string | undefined;

const MAX_SESSION_MS = 3 * 60 * 1000;        // 3 minutes total cap
const SILENCE_TIMEOUT_MS = 25 * 1000;        // 25s of quiet → stop
const SILENCE_LEVEL_THRESHOLD = 0.04;        // below this RMS counts as silence
const LEVEL_SAMPLE_HZ = 30;                  // analyser sampling rate

function buildWsUrl(token: string): string {
  const base = (apiServerUrl ?? '').replace(/^http/, 'ws').replace(/\/$/, '');
  return `${base}/api/transcribe/stream?token=${encodeURIComponent(token)}`;
}

export function useVoiceDictation(): UseVoiceDictationResult {
  const { getAccessTokenSilently } = useAuth0();

  const serviceRef = useRef<RealtimeTranscriptionService | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserCtxRef = useRef<AudioContext | null>(null);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);
  const levelRafRef = useRef<number | null>(null);
  const lastSampleAtRef = useRef(0);
  const lastSpokeAtRef = useRef(0);
  const sessionStartedAtRef = useRef(0);
  const guardTimersRef = useRef<{ session: number | null; silence: number | null }>({
    session: null,
    silence: null,
  });
  const autoStopReasonRef = useRef<string | null>(null);
  // True when the user pressed stop themselves; lets us suppress the
  // "connection lost" message that the downstream WS close would otherwise fire.
  const intentionalStopRef = useRef(false);

  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const clearGuards = useCallback(() => {
    if (guardTimersRef.current.session !== null) {
      window.clearTimeout(guardTimersRef.current.session);
      guardTimersRef.current.session = null;
    }
    if (guardTimersRef.current.silence !== null) {
      window.clearTimeout(guardTimersRef.current.silence);
      guardTimersRef.current.silence = null;
    }
    if (levelRafRef.current !== null) {
      cancelAnimationFrame(levelRafRef.current);
      levelRafRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    clearGuards();

    serviceRef.current?.disconnect();
    serviceRef.current = null;

    if (analyserCtxRef.current && analyserCtxRef.current.state !== 'closed') {
      analyserCtxRef.current.close().catch(() => undefined);
    }
    analyserCtxRef.current = null;
    analyserNodeRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    setIsRecording(false);
    setIsConnecting(false);
    setLevel(0);

    if (intentionalStopRef.current) {
      // User pressed stop. Any pending auto-stop reason from the disconnect
      // dance is irrelevant — clear both the reason and any visible error.
      autoStopReasonRef.current = null;
      setError(null);
    } else if (autoStopReasonRef.current) {
      setError(autoStopReasonRef.current);
      autoStopReasonRef.current = null;
    }
    // NOTE: do NOT reset intentionalStopRef here. If the backend's WS close
    // arrives after our 250ms await, the next 'disconnected' event must still
    // know the user stopped on purpose. We reset this flag in start() instead.
  }, [clearGuards]);

  // Cleanup on unmount.
  useEffect(() => () => teardown(), [teardown]);

  // Tab visibility + page unload guardrails — only active during a recording.
  useEffect(() => {
    if (!isRecording) return;

    const onVisibility = () => {
      if (document.hidden) {
        autoStopReasonRef.current = 'Dictation stopped because the tab was hidden.';
        teardown();
      }
    };
    const onBeforeUnload = () => teardown();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [isRecording, teardown]);

  /** Spin up an analyser on the mic stream to drive `level` and silence detection. */
  const startAnalyser = (stream: MediaStream) => {
    const ctx = new AudioContext();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.85;
    source.connect(analyser);

    analyserCtxRef.current = ctx;
    analyserNodeRef.current = analyser;

    const buf = new Uint8Array(analyser.fftSize);
    const sampleIntervalMs = 1000 / LEVEL_SAMPLE_HZ;

    const tick = (now: number) => {
      levelRafRef.current = requestAnimationFrame(tick);
      if (now - lastSampleAtRef.current < sampleIntervalMs) return;
      lastSampleAtRef.current = now;

      analyser.getByteTimeDomainData(buf);
      // RMS of zero-centered samples in [-1, 1].
      let sumSq = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sumSq += v * v;
      }
      const rms = Math.sqrt(sumSq / buf.length);
      // Mild non-linear curve so quiet speech still moves the meter.
      const normalized = Math.min(1, Math.pow(rms * 3, 0.8));
      setLevel(normalized);

      if (rms >= SILENCE_LEVEL_THRESHOLD) {
        lastSpokeAtRef.current = now;
      }
    };

    levelRafRef.current = requestAnimationFrame(tick);
  };

  const armGuards = () => {
    sessionStartedAtRef.current = performance.now();
    lastSpokeAtRef.current = performance.now();

    guardTimersRef.current.session = window.setTimeout(() => {
      autoStopReasonRef.current = 'Dictation reached the 3 minute limit.';
      teardown();
    }, MAX_SESSION_MS);

    const checkSilence = () => {
      const idleMs = performance.now() - lastSpokeAtRef.current;
      if (idleMs >= SILENCE_TIMEOUT_MS) {
        autoStopReasonRef.current = 'Dictation paused after no speech was detected.';
        teardown();
        return;
      }
      // Re-arm to check again at the remaining gap.
      const remaining = Math.max(1000, SILENCE_TIMEOUT_MS - idleMs);
      guardTimersRef.current.silence = window.setTimeout(checkSilence, remaining);
    };
    guardTimersRef.current.silence = window.setTimeout(checkSilence, SILENCE_TIMEOUT_MS);
  };

  const start = useCallback(async () => {
    if (isRecording || isConnecting) return;
    setError(null);
    setIsConnecting(true);
    // Reset the intentional-stop flag for the new session. (It's left sticky
    // by teardown so a delayed WS-close after stop() doesn't surface an error.)
    intentionalStopRef.current = false;
    autoStopReasonRef.current = null;

    try {
      const token = await getAccessTokenSilently();
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = audioStream;

      const service = new RealtimeTranscriptionService();
      serviceRef.current = service;

      service.onEvent((event: TranscriptionEvent) => {
        switch (event.type) {
          case 'transcript_partial':
            setInterim(event.data?.text?.trim() ?? '');
            break;
          case 'transcript_final': {
            const text = event.data?.text?.trim() ?? '';
            if (text) setTranscript((prev) => (prev ? `${prev} ${text}` : text));
            setInterim('');
            break;
          }
          case 'error':
            setError(event.data?.message ?? 'Transcription error');
            teardown();
            break;
          case 'disconnected':
            // If the user stopped on purpose this fires after our own
            // disconnect; don't surface that as an error.
            if (!intentionalStopRef.current && !autoStopReasonRef.current) {
              autoStopReasonRef.current = 'Connection to dictation server lost.';
            }
            teardown();
            break;
        }
      });

      await service.connect(buildWsUrl(token));
      service.startStreaming(audioStream);
      startAnalyser(audioStream);
      armGuards();

      setIsConnecting(false);
      setIsRecording(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start dictation');
      teardown();
    }
  }, [isRecording, isConnecting, getAccessTokenSilently, teardown]);

  const stop = useCallback(async () => {
    if (!serviceRef.current) return;
    intentionalStopRef.current = true;
    clearGuards();
    serviceRef.current.stopStreaming();
    serviceRef.current.finalize();
    // Let the backend flush transcript_final before we kill the socket.
    await new Promise((r) => setTimeout(r, 250));
    teardown();
  }, [clearGuards, teardown]);

  const reset = useCallback(() => {
    setTranscript('');
    setInterim('');
    setError(null);
  }, []);

  return { isRecording, isConnecting, transcript, interim, level, error, start, stop, reset };
}
